import React, {useState} from 'react';
import {Modal, Button, Spinner, Alert} from 'react-bootstrap';
import {EmailAccount} from '../DomainEmailAccounts';

interface DeleteEmailAccountModalProps {
  show: boolean;
  onHide: () => void;
  account: EmailAccount | null;
  onConfirm: () => Promise<void>;
}

const DeleteEmailAccountModal = ({
  show,
  onHide,
  account,
  onConfirm,
}: DeleteEmailAccountModalProps): JSX.Element => {
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  const handleConfirm = async () => {
    setIsLoading(true);
    setApiError('');

    try {
      await onConfirm();
      onHide();
    } catch (error: any) {
      console.error('Erreur lors de la suppression:', error);

      // Gestion des messages d'erreur
      if (error.response?.data?.message) {
        setApiError(error.response.data.message);
      } else if (error.response?.status === 404) {
        setApiError('Le compte n\'existe pas ou a déjà été supprimé');
      } else if (error.response?.status === 403) {
        setApiError('Vous n\'avez pas les droits pour supprimer ce compte');
      } else {
        setApiError('Erreur lors de la suppression du compte. Veuillez réessayer');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setApiError('');
      onHide();
    }
  };

  if (!account) return <></>;

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton className="text-white">
        <Modal.Title>
          <i className="fa-solid fa-trash me-4"></i>
          Supprimer le compte de messagerie
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {apiError && (
          <Alert variant="danger" dismissible onClose={() => setApiError('')}>
            <i className="fa-solid fa-circle-exclamation me-2"></i>
            {apiError}
          </Alert>
        )}

        <div className="alert alert-danger d-flex align-items-start">
          <i className="fa-solid fa-exclamation-triangle fs-2 me-3"></i>
          <div>
            <h6 className="mb-2">Attention, cette action est irréversible !</h6>
            <p className="mb-0">
              Vous êtes sur le point de supprimer définitivement le compte de messagerie suivant :
            </p>
          </div>
        </div>

        <div className="p-3 bg-light rounded mb-3">
          <div className="mb-2">
            <strong>Compte :</strong> <span className="text-primary">{account.username}</span>
          </div>
          <div className="mb-2">
            <strong>Nom :</strong> {account.name}
          </div>
          <div className="mb-2">
            <strong>État :</strong>{' '}
            <span className={`badge ${account.active ? 'badge-success' : 'badge-danger'}`}>
              {account.active ? 'Actif' : 'Inactif'}
            </span>
          </div>
          {account.aliases && account.aliases.length > 0 && (
            <div>
              <strong>Alias associés :</strong>
              <ul className="mb-0 mt-1">
                {account.aliases.map((aliasObj, index) => (
                  <li key={index}>{aliasObj.alias}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="alert alert-warning mb-0 text-dark">
          <i className="fa-solid fa-info-circle me-2"></i>
          <strong>Conséquences de la suppression :</strong>
          <ul className="mb-0 mt-2">
            <li>Tous les emails du compte seront supprimés</li>
            <li>Les alias associés seront également supprimés</li>
            <li>Les règles de messagerie associées seront perdues</li>
            <li>Cette action ne peut pas être annulée</li>
          </ul>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
          <i className="fa-solid fa-xmark me-2"></i>
          Annuler
        </Button>
        <Button variant="danger" onClick={handleConfirm} disabled={isLoading}>
          {isLoading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Suppression en cours...
            </>
          ) : (
            <>
              <i className="fa-solid fa-trash me-2"></i>
              Confirmer la suppression
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DeleteEmailAccountModal;